import styled from "styled-components";
import "../i18n";
import { useState, useEffect, useCallback } from "react";
import { t, changeLanguage } from "i18next";
import * as GoalsRestClient from "../services/GoalsRestClient";
import { HkkDivisionProgress } from "../services/GoalsRestClient";
import { useSearchParams } from "react-router-dom";
import { COLOR } from "../constants/theme";
import logo from "../assets/img/hkk-logo.png";

type LoadingState = "loading" | "loaded" | "error";

const Microsite = () => {
  const [searchParams] = useSearchParams();
  const [divisions, setDivisions] = useState<HkkDivisionProgress[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>("loading");
  const [showAllDivisions, setShowAllDivisions] = useState(false);

  const loadHkkProgress = useCallback(async (userId: string | null) => {
    setLoadingState("loading");
    try {
      const response = await GoalsRestClient.getHkkProgress(userId);
      setDivisions(response.divisions);
      setLoadingState("loaded");
    } catch (e) {
      console.error("Failed to load HKK progress:", e);
      setLoadingState("error");
    }
  }, []);

  useEffect(() => {
    const locale = searchParams.get("locale") || navigator.language || "de";
    changeLanguage(locale.substring(0, 2));

    const userId = searchParams.get("userId");
    loadHkkProgress(userId);
  }, [searchParams, loadHkkProgress]);

  const renderDivisionRow = (division: HkkDivisionProgress, index: number) => {
    const isHighlighted = division.userInDivision === true;
    return (
      <div key={division.id}>
        <RankingListItem
          backgroundColor={isHighlighted ? `${COLOR}1A` : "#FFFFFF"}
        >
          <RankCell>
            <Text
              fontSize={14}
              fontColor={"#000000"}
              fontWeight={isHighlighted ? "bold" : "normal"}
            >
              {division.rank}.
            </Text>
          </RankCell>
          <NameCell>
            <Text
              fontSize={14}
              fontColor={"#000000"}
              fontWeight={isHighlighted ? "bold" : "normal"}
            >
              {division.name}
            </Text>
          </NameCell>
          <StepsCell>
            <Text
              fontSize={14}
              fontColor={"#666666"}
              fontWeight={isHighlighted ? "bold" : "normal"}
            >
              {Math.round(division.averageSteps).toLocaleString("de-DE")}
            </Text>
          </StepsCell>
          <GoalCell>
            <Text fontSize={14} fontColor={COLOR} fontWeight={"bold"}>
              {division.participantsReachedGoal}
            </Text>
          </GoalCell>
          <PercentageCell>
            <Text fontSize={12} fontColor={"#666666"} fontWeight={"normal"}>
              {division.percentage.toFixed(2)}%
            </Text>
          </PercentageCell>
        </RankingListItem>
        {index < divisions.length - 1 && <HorizontalRule />}
      </div>
    );
  };

  const visibleDivisions = showAllDivisions
    ? divisions
    : divisions.slice(0, 10);
  const hasMoreDivisions = divisions.length > 10;

  // Find if user's division is outside top 10
  const userDivisionIndex = divisions.findIndex((d) => d.userInDivision);
  const userDivisionOutsideTop10 = userDivisionIndex >= 10;
  const userDivision = userDivisionOutsideTop10
    ? divisions[userDivisionIndex]
    : null;

  return (
    <Container>
      <BackgroundHeader />
      <VerticalGridContainer style={{ marginTop: -35 }}>
        <TableContainer>
          <TitleContainer>
            <Text fontSize={18} fontColor={"#000000"} fontWeight={"bold"}>
              {t("DIVISION_RANKING")}
            </Text>
          </TitleContainer>

          {/* Table Header */}
          <TableHeader>
            <RankCell>
              <HeaderText>#</HeaderText>
            </RankCell>
            <NameCell>
              <HeaderText>{t("COMPANY")}</HeaderText>
            </NameCell>
            <StepsCell>
              <HeaderText>{t("AVG_STEPS_PARTICIPANTS")}</HeaderText>
            </StepsCell>
            <GoalCell>
              <HeaderText>{t("REACHED_GOAL")}</HeaderText>
            </GoalCell>
            <PercentageCell>
              <HeaderText>{t("PERCENT")}</HeaderText>
            </PercentageCell>
          </TableHeader>
          <HorizontalRule />

          {loadingState === "loading" && (
            <LoadingContainer>
              <Text fontSize={14} fontColor={"#666666"}>
                {t("LOADING")}
              </Text>
            </LoadingContainer>
          )}

          {loadingState === "error" && (
            <ErrorContainer>
              <Text fontSize={14} fontColor={"#cc0000"}>
                {t("DATA_UNAVAILABLE")}
              </Text>
            </ErrorContainer>
          )}

          {loadingState === "loaded" && (
            <>
              {visibleDivisions.map((division, index) =>
                renderDivisionRow(division, index),
              )}

              {/* Show user's division if outside top 10 and not showing all */}
              {!showAllDivisions && userDivision && (
                <>
                  <EllipsisRow>
                    <Text fontSize={14} fontColor={"#999999"}>
                      ...
                    </Text>
                  </EllipsisRow>
                  {renderDivisionRow(userDivision, divisions.length - 1)}
                </>
              )}

              {hasMoreDivisions && (
                <RankingListItem
                  justifyContent={"flex-end"}
                  padding={8}
                  onClick={() => setShowAllDivisions(!showAllDivisions)}
                  style={{ cursor: "pointer" }}
                >
                  <Text fontSize={14} fontWeight={"bold"} fontColor={COLOR}>
                    {showAllDivisions ? `${t("SEE_LESS")} ↑` : `${t("MORE")} ↓`}
                  </Text>
                </RankingListItem>
              )}
            </>
          )}
        </TableContainer>
      </VerticalGridContainer>
    </Container>
  );
};

type TextProps = {
  fontSize?: number;
  fontColor?: string;
  fontWeight?: string;
};

type RankingListItemProps = {
  padding?: number;
  justifyContent?: string;
  backgroundColor?: string;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f8f8f8;
  min-height: 100vh;
`;

const BackgroundHeader = styled.div`
  background-color: ${COLOR};
  min-height: 130px;
  width: 100%;
  display: flex;
  background-image: url(${logo});
  background-size: 130px;
  background-repeat: no-repeat;
  background-position-y: 20px;
  background-position-x: 8px;
`;

const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 250px;
  width: 100%;
  max-width: 600px;
  overflow: hidden;
  background-color: #ffffff;
  margin-left: 16px;
  margin-right: 16px;
  border: 1px solid #eeeeee;
  border-radius: 15px;
`;

const Text = styled.span<TextProps>`
  color: ${(props) => props.fontColor || "#000000"};
  font-weight: ${(props) => props.fontWeight || "normal"};
  font-size: ${(props) => props.fontSize || 14}px;
  margin: 0;
  font-family: "Ubuntu", sans-serif;
`;

const HeaderText = styled.span`
  color: #666666;
  font-weight: 600;
  font-size: 12px;
  font-family: "Ubuntu", sans-serif;
  text-transform: uppercase;
  display: block;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;

  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const VerticalGridContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 8px;
`;

const HorizontalRule = styled.hr`
  margin: 0;
  border: 0;
  background-color: #f0f0f0;
  width: 100%;
  height: 1px;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #dbdbdb;
  width: 100%;
  border-radius: 15px 15px 0 0;
  padding: 12px;
  box-sizing: border-box;
`;

const TableHeader = styled.div`
  display: flex;
  flex-direction: row;
  padding: 8px 12px;
  background-color: #fafafa;

  @media (max-width: 480px) {
    padding: 8px 8px;
  }
`;

const RankingListItem = styled.div<RankingListItemProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${(props) => props.padding || 12}px;
  justify-content: ${(props) => props.justifyContent || "flex-start"};
  width: 100%;
  background-color: ${(props) => props.backgroundColor || "white"};
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: ${(props) => props.padding || 8}px;
  }
`;

const RankCell = styled.div`
  min-width: 35px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    min-width: 25px;
    padding-right: 4px;
  }
`;

const NameCell = styled.div`
  flex: 1;
  min-width: 0;
  padding-right: 8px;
  overflow-wrap: break-word;
  word-break: break-word;

  @media (max-width: 480px) {
    padding-right: 4px;
  }
`;

const StepsCell = styled.div`
  width: 120px;
  text-align: center;
  padding-left: 8px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 55px;
    padding-left: 4px;
  }
`;

const GoalCell = styled.div`
  width: 120px;
  margin-left: 8px;
  text-align: center;
  flex-shrink: 0;
  overflow: hidden;

  @media (max-width: 480px) {
    width: 40px;
    margin-left: 4px;
  }
`;

const PercentageCell = styled.div`
  width: 60px;
  margin-left: 8px;
  text-align: right;
  flex-shrink: 0;
  overflow: hidden;

  @media (max-width: 480px) {
    width: 45px;
    margin-left: 4px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;

const EllipsisRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px;
  background-color: #fafafa;
`;

export default Microsite;
